import { memo, useCallback } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { useDebouncedCallback } from 'use-debounce';

import { useShortIdContext } from '@air/provider-short-id';

import { PreviousAndNextAssetModalArrows } from '~/components/PreviousAndNextAssetModalArrows';
import { useGoToPublicAssetPage } from '~/hooks/useGoToPublicAssetPage';
import { usePublicMediaPlayerData } from '~/swr-hooks/mediaPlayer/usePublicMediaPlayerData';

const ASSET_NAVIGATION_DEBOUNCE_MS = 100;

export const PublicPreviousAndNextArrows = memo(() => {
  const { shortId } = useShortIdContext();
  const { loadMore, nextAsset, previousAsset, isNearEnd } = usePublicMediaPlayerData(shortId);
  const { goToAssetPage } = useGoToPublicAssetPage();

  const onPreviousClick = useCallback(() => {
    if (previousAsset) {
      goToAssetPage({
        asset: previousAsset,
      });
    }
  }, [goToAssetPage, previousAsset]);

  const onNextClick = useCallback(() => {
    if (nextAsset) {
      goToAssetPage({
        asset: nextAsset,
      });
      if (isNearEnd) {
        loadMore();
      }
    }
  }, [goToAssetPage, isNearEnd, loadMore, nextAsset]);

  const [delayedOnPrevious] = useDebouncedCallback(onPreviousClick, ASSET_NAVIGATION_DEBOUNCE_MS);
  const [delayedOnNext] = useDebouncedCallback(onNextClick, ASSET_NAVIGATION_DEBOUNCE_MS);

  useHotkeys(
    'left,right',
    (_keyboardEvent, hotkeysEvent) => {
      if (hotkeysEvent.keys?.includes('left')) {
        delayedOnPrevious();
      } else if (hotkeysEvent.keys?.includes('right')) {
        delayedOnNext();
      }
    },
    [delayedOnNext, delayedOnPrevious, !!nextAsset, !!previousAsset],
  );

  return (
    <PreviousAndNextAssetModalArrows
      onNextClick={nextAsset ? onNextClick : undefined}
      onPreviousClick={previousAsset ? onPreviousClick : undefined}
    />
  );
});

PublicPreviousAndNextArrows.displayName = 'PublicPreviousAndNextArrows';
